import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { SpotCard } from "@/components/OfflineSpotsSection";
import OfflineSpotComposer from "@/components/OfflineSpotComposer";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 24;
const ADMIN_EMAIL = "swarajrp030@gmail.com";

// Full "offline spots" listing, filtered by the same tabs as the feed.
const OfflineSpots = () => {
  const navigate = useNavigate();
  const [spots, setSpots] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("all");
  const [user, setUser] = useState(null);
  // "" means every city. Seeded from the profile so the default view is local,
  // with an explicit escape hatch below for browsing everywhere.
  const [city, setCity] = useState("");
  const [cityReady, setCityReady] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        setCity((r.data?.city || "").trim());
      })
      .catch(() => setUser(null))
      // Wait for auth before the first fetch, or we'd load every city and then
      // immediately reload scoped to theirs.
      .finally(() => setCityReady(true));
  }, []);

  const fetchSpots = useCallback(() => {
    if (!cityReady) return;
    setLoading(true);
    axios
      .get(`${API}/offline-spots`, {
        params: { category_id: tab, limit: PAGE_SIZE, city: city || undefined },
      })
      .then((res) => {
        setSpots(res.data?.items || []);
        setTotal(res.data?.total || 0);
      })
      .catch(() => {
        setSpots([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [tab, city, cityReady]);

  useEffect(fetchSpots, [fetchSpots]);

  const sizeLabelFor = (spot) => {
    const cat = categories.find((c) => c.id === spot.category_id);
    return (cat?.size_label || (spot.kind === "shoes" ? "UK" : "ft")).trim();
  };

  const canDelete = (spot) =>
    user && (user.user_id === spot.user_id || user.email === ADMIN_EMAIL);

  const handleDelete = async (spot) => {
    if (!window.confirm(`Remove "${spot.store_name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/offline-spots/${spot.id}`, { withCredentials: true });
      track("offline_spot_delete", { spot_id: spot.id, is_own: user?.user_id === spot.user_id });
      setSpots((prev) => prev.filter((s) => s.id !== spot.id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Spot removed");
    } catch (e) {
      toast.error("Could not remove this spot");
    }
  };

  const share = () => {
    track("offline_spot_share_click", { source: "offline_spots_page", is_logged_in: !!user });
    if (!user) {
      toast.error("Please sign in to share a spot");
      navigate("/login");
      return;
    }
    setComposerOpen(true);
  };

  const tabs = [{ id: "all", name: "All" }, ...categories];

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="offline-spots-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1
          className="text-2xl md:text-3xl tracking-tighter ml-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          North of Six Club
        </h1>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-16">
        {/* Stacked on mobile: the heading wraps to two lines there, and a
            button sitting beside the second line leaves dead space above it.
            Desktop has the width to keep them on one row. */}
        <div className="flex flex-col items-start gap-4 mb-8 md:flex-row md:items-end md:justify-between">
          <div>
            <p
              className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#6B6A68] mb-2"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Offline spots
            </p>
            <h2
              className="text-3xl md:text-5xl tracking-tighter"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Where to buy in person
            </h2>
          </div>
          <button
            type="button"
            onClick={share}
            className="shrink-0 flex items-center gap-2 bg-[#2D302A] text-white px-4 py-3 rounded-md hover:bg-[#1A1C18] transition-colors"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid="offline-spots-page-share"
          >
            <Plus size={16} weight="bold" />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.15em]">Add a spot</span>
          </button>
        </div>

        {/* City scope. Only shown when the profile has a city — without one
            there is nothing to switch between. */}
        {(user?.city || "").trim() && (
          <div className="flex items-center gap-2 mb-5" data-testid="offline-spots-city-scope">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
              Showing
            </span>
            {[
              { key: user.city.trim(), label: user.city.trim() },
              { key: "", label: "All cities" },
            ].map((o) => (
              <button
                key={o.label}
                type="button"
                onClick={() => {
                  track("offline_spot_city_scope", { scope: o.key || "all" });
                  setCity(o.key);
                }}
                className={`px-3 py-1.5 rounded-md border text-[10px] md:text-xs uppercase tracking-[0.12em] transition-colors ${
                  city === o.key
                    ? "bg-[#2D302A] text-white border-[#2D302A]"
                    : "bg-white text-[#2D302A] border-[#E4E1D9] hover:border-[#C9C5BA]"
                }`}
                style={{ fontFamily: "IBM Plex Mono, monospace" }}
                data-testid={`offline-spots-city-${o.key ? "mine" : "all"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}

        {/* Tab filter — same tabs as the feed */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8 -mx-6 px-6 scroll-pl-6 md:mx-0 md:px-0 md:scroll-pl-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                track("offline_spot_tab_select", { tab_id: t.id, tab_name: t.name });
                setTab(t.id);
              }}
              className={`shrink-0 px-4 py-2 rounded-md border text-[10px] md:text-xs uppercase tracking-[0.15em] transition-colors ${
                tab === t.id
                  ? "bg-[#2D302A] text-white border-[#2D302A]"
                  : "bg-white text-[#2D302A] border-[#E4E1D9] hover:border-[#C9C5BA]"
              }`}
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid={`offline-spots-tab-${t.id}`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-56 bg-[#F0EFEB] rounded-md animate-pulse" />
            ))}
          </div>
        ) : spots.length === 0 ? (
          <p className="text-sm text-[#6B6A68]">
            {city ? `No spots in ${city} under this tab yet.` : "No spots here yet."}{" "}
            Know a store where things actually fit?{" "}
            <button onClick={share} className="underline hover:no-underline">
              Add the first one.
            </button>
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {spots.map((s) => (
              <div key={s.id} className="relative group">
                <SpotCard
                  spot={s}
                  sizeLabelFor={sizeLabelFor}
                  onOpen={() => {
                    track("offline_spot_open", { spot_id: s.id, source: "offline_spots_page" });
                    navigate(`/offline-spots/${s.id}`);
                  }}
                />
                {canDelete(s) && (
                  <button
                    type="button"
                    onClick={() => handleDelete(s)}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm hover:bg-white p-2 rounded-full shadow transition-all hover:scale-110"
                    title="Remove spot"
                    aria-label="Remove spot"
                    data-testid="offline-spot-delete"
                  >
                    <Trash size={14} weight="bold" className="text-red-600" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {total > spots.length && (
          <p
            className="mt-8 text-xs text-[#9A9A98]"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            Showing {spots.length} of {total}
          </p>
        )}
      </main>

      {composerOpen && user && (
        <OfflineSpotComposer
          open={composerOpen}
          user={user}
          categories={categories}
          onClose={() => setComposerOpen(false)}
          onSuccess={fetchSpots}
        />
      )}
    </div>
  );
};

export default OfflineSpots;
